'use client';

import { useEffect, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SearchCardComponent } from './search-card/search-card.component';
import { SearchScheduleEmptyState } from './search-schedule-empty-state.component';

import { hasRenderableTelemedSuggestions, TelemedProcedure } from '@/app/(main)/(pages)/busca/[slug]/_services/get-telemed-procedure.ts.service';
import { hasRenderableNearCitySuggestions, NearCitySchedules } from '@/app/(main)/(pages)/busca/[slug]/_services/get-near-city-schedules.service';
import { hasRenderableCityBranchSuggestions, CityBranches } from '@/app/(main)/(pages)/busca/[slug]/_services/get-city-branches.service';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import { Button } from '@/components/ui/button/button.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { useToast } from '@/components/ui/use-toast';
import { useLoading } from '@/hooks/use-loading.hook';
import { useUserLocation } from '@/hooks/use-user-location.hook';
import { useSearchFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-filters.hook';
import { getSchedulesFromSearchApi } from '@/app/(main)/(pages)/busca/[slug]/_services/get-schedules-from-search-api.service';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { SearchSuggestions } from '@/app/(main)/(pages)/busca/[slug]/_components/search-suggestions/search-suggestions.component';
import { TelehealthSuggestion } from '@/app/(main)/(pages)/busca/[slug]/_components/search-suggestions/telehealth-suggestion.component';
import { NearCitySuggestion } from '@/app/(main)/(pages)/busca/[slug]/_components/search-suggestions/near-city-suggestion.component';
import { getActiveFilters } from '@/app/(main)/(pages)/busca/[slug]/_utils/active-filters.util';
import { getFilterAnalyticsProperties } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-analytics.util';
import { getAvailableFilters } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-availability.util';
import { SearchType } from '@/types/most-search-type';
import { CityBranchesSuggestion } from '@/app/(main)/(pages)/busca/[slug]/_components/search-suggestions/city-branches-suggestion.component';
import { minResultsToHideSuggestions } from '@/utils/consts/search-schedules.consts';
import { isBlockedTelehealthCity } from '@/app/(main)/(pages)/busca/[slug]/_consts/blocked-telehealth-cities.const';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';

interface Props {
  searchType: string;
  modality: SearchType;
  schedules: SearchPartnerData[];
  hasMore: boolean;
  telehealthSuggestions: TelemedProcedure | null;
  nearCitySchedules: NearCitySchedules | null;
  cityBranches: CityBranches | null;
  procedureName: string;
  proceduresResearched: CodesOrSlugsResponse;
  shouldTrackView?: boolean;
  onViewTracked?: () => void;
}

export function SearchSchedulesList({
  schedules,
  hasMore,
  searchType,
  modality,
  telehealthSuggestions,
  nearCitySchedules,
  cityBranches,
  procedureName,
  proceduresResearched,
  shouldTrackView = true,
  onViewTracked,
}: Props) {
  const [searchSchedules, setSearchSchedules] = useState<SearchPartnerData[]>(schedules);
  const [page, setPage] = useState<number>(1);
  const [hasMoreSchedules, setHasMoreSchedules] = useState(hasMore);
  const { userLocation } = useUserLocation();

  const { isLoading, startLoading, stopLoading } = useLoading();
  const { toast } = useToast();
  const analytics = getAnalyticsPlugin();
  const { value: showsExpandedGrid } = useFeatureFlag('shouldShowExpandedGrid');
  const { filters } = useSearchFilter();

  const availableFilters = getAvailableFilters(filters, modality);
  const hasTelehealthSuggestions = hasRenderableTelemedSuggestions(telehealthSuggestions);
  const isTelehealthBlocked = isBlockedTelehealthCity(userLocation?.city, userLocation?.state);
  const hasFewCityResults = !hasMore && schedules.length < minResultsToHideSuggestions;
  const shouldShowTelehealthSuggestions = hasFewCityResults && hasTelehealthSuggestions && !isTelehealthBlocked;
  const hasNearCitySuggestions = hasRenderableNearCitySuggestions(nearCitySchedules);
  const shouldShowNearCity = hasFewCityResults && hasNearCitySuggestions;
  const hasCityBranchSuggestions = hasRenderableCityBranchSuggestions(cityBranches);
  const shouldShowCityBranches = hasFewCityResults && hasCityBranchSuggestions;
  const showSuggestions = shouldShowTelehealthSuggestions || shouldShowNearCity || shouldShowCityBranches;
  const hasResults = searchSchedules.length > 0;
  const hasNoResults = !hasResults && !showSuggestions;
  const activeFilters = getActiveFilters(filters, modality);
  const procedureSlugs = proceduresResearched.map((procedure) => procedure.slug);

  useEffect(() => {
    if (!shouldTrackView) {
      return;
    }
    onViewTracked?.();

    if (hasFewCityResults) {
      analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_TELEHEALTH_SHOWN, {
        qtd_opcoes_cidade: schedules.length,
        tem_tele_relacionada: hasTelehealthSuggestions,
        procedure_code: proceduresResearched[0]?.code,
        city: userLocation?.city,
      });
    }

    if (shouldShowNearCity) {
      analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_NEAR_CITY_SHOWN, {
        qtd_cidades: nearCitySchedules?.cityCount,
        city_origem: userLocation?.city,
        procedure_code: proceduresResearched[0]?.code,
      });
    }

    if (shouldShowCityBranches) {
      analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_CITY_BRANCH_SHOWN, {
        qtd_unidades: cityBranches?.count,
        city: userLocation?.city,
        procedure_code: proceduresResearched[0]?.code,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMoreSchedules = async (): Promise<void> => {
    analytics.track(AnalyticsEnum.CLICK_SEE_MORE_SEARCH_RESULTS);

    try {
      startLoading();
      const nextPage = page + 1;

      const { schedules: newSchedules, hasMore: hasMoreResult } = await getSchedulesFromSearchApi({
        slugs: procedureSlugs,
        page: nextPage,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        city: userLocation?.city,
        state: userLocation?.state,
        distance: availableFilters.distance,
        orderBy: filters.orderBy,
        ageGroup: availableFilters.ageGroup,
        dayTime: availableFilters.dayTime,
        professionalGender: availableFilters.professionalGender,
        minDate: availableFilters.minDate,
        maxDate: availableFilters.maxDate,
        followUp: availableFilters.followUp,
        onlyScheduled: availableFilters.onlyScheduled,
        searchType,
      });

      setSearchSchedules([...searchSchedules, ...newSchedules]);
      setPage(nextPage);
      setHasMoreSchedules(hasMoreResult && newSchedules.length > 0);
    } catch {
      toast.error('Erro ao carregar mais resultados.');
    } finally {
      stopLoading();
    }
  };

  return (
    <>
      {shouldTrackView && (
        <TrackUserView
          eventName={AnalyticsEnum.VIEW_SEARCH_RESULTS}
          properties={{
            variante: showsExpandedGrid ? 'B' : 'A',
            ...(hasNoResults
              ? {
                  tinha_filtro_aplicado: activeFilters.length > 0,
                  ...getFilterAnalyticsProperties(filters, modality),
                }
              : {}),
          }}
        />
      )}
      <div className="w-full">
        {searchSchedules.length > 0 && (
          <div className="w-full flex flex-col items-center gap-6 pb-6">
            {searchSchedules.map((schedule, index) => (
              <SearchCardComponent
                key={`${schedule.partner.id}-${schedule.professional?.id ?? 'no-prof'}-${index}`}
                schedule={schedule}
                searchType={searchType}
                proceduresResearched={proceduresResearched}
              />
            ))}
          </div>
        )}

        {hasMoreSchedules && (
          <ContentContainer className="md:px-0 md:mx-auto md:max-w-[576px] lg:max-w-3xl flex justify-center items-center mt-5 mb-6 md:mt-0">
            <Button
              data-testid={DataTestIdEnum.SEARCH_PAGE_SEE_MORE}
              variant="outline"
              className="w-full xl:w-80"
              isLoading={isLoading}
              onClick={fetchMoreSchedules}
            >
              Ver mais resultados
            </Button>
          </ContentContainer>
        )}

        {!hasResults && (
          <SearchScheduleEmptyState
            procedureName={procedureName}
            searchType={searchType}
            procedureCount={proceduresResearched.length}
            showNotifyForm={!showSuggestions}
          />
        )}

        {showSuggestions && (
          <SearchSuggestions hasResults={hasResults}>
            {shouldShowCityBranches && (
              <CityBranchesSuggestion cityBranches={cityBranches} />
            )}
            {shouldShowNearCity && (
              <NearCitySuggestion
                nearCitySchedules={nearCitySchedules}
                searchType={searchType}
                proceduresResearched={proceduresResearched}
              />
            )}
            {shouldShowTelehealthSuggestions && (
              <TelehealthSuggestion
                telehealthSuggestions={telehealthSuggestions}
                proceduresResearched={proceduresResearched}
              />
            )}
          </SearchSuggestions>
        )}
      </div>
    </>
  );
}
